import type {
  ConsultantDeadlineBucket,
  ConsultantDeadlineRow,
} from '@shared/dto/deadline/consultant-deadline.dto';
import { parseDateKey } from '../../../components/calendar/calendar-utils';
import { isAttachRenewal } from '../../documents-archive/deadlines/renew-mode';

export type BucketChipColor = 'error' | 'warning' | 'info' | 'success' | 'default';

export const RELATIVE_DUE_MAX_DAYS = 60;

/** Riga del consulente gestita da me o da un collega del mio studio: per le azioni dei gestori vale lo stesso. */
export function isManagedByMyStudio(
  row: Pick<ConsultantDeadlineRow, 'isManagedByMe' | 'managedByTeammate'>,
): boolean {
  return row.isManagedByMe || row.managedByTeammate;
}

export type RowPrimaryAction = 'renew' | 'attach';

/** Una sola azione in evidenza sulla riga; il menu resta per tutto il resto. */
export function resolveRowPrimaryAction(
  row: ConsultantDeadlineRow,
  todayIso: string,
): RowPrimaryAction | null {
  if (row.archivedAt) {
    return null;
  }
  if (row.managedBy === 'CONSULTANT' && !isManagedByMyStudio(row)) {
    return null;
  }
  const attachCandidate = {
    validityMonths: row.validityMonths,
    dueAt: row.dueAt,
    hasCurrentFile: row.hasDocument,
  };
  if (isAttachRenewal(attachCandidate, todayIso)) {
    return 'attach';
  }
  if (row.bucket === 'EXPIRED' || row.bucket === 'EXPIRING' || row.awaitingDecision) {
    return 'renew';
  }
  return null;
}

export type RelativeDue =
  | { kind: 'today' }
  | { kind: 'overdue'; days: number }
  | { kind: 'dueIn'; days: number }
  | { kind: 'far'; isPast: boolean };

function toUtcDayNumber(isoDay: string): number {
  const [year, month, day] = isoDay.slice(0, 10).split('-').map(Number);
  return Date.UTC(year, month - 1, day) / 86_400_000;
}

export function describeRelativeDue(dueAtIso: string, todayIso: string): RelativeDue {
  const daysUntilDue = toUtcDayNumber(dueAtIso) - toUtcDayNumber(todayIso);
  if (Math.abs(daysUntilDue) > RELATIVE_DUE_MAX_DAYS) {
    return { kind: 'far', isPast: daysUntilDue < 0 };
  }
  if (daysUntilDue === 0) {
    return { kind: 'today' };
  }
  return daysUntilDue < 0
    ? { kind: 'overdue', days: -daysUntilDue }
    : { kind: 'dueIn', days: daysUntilDue };
}

export function getDeadlineRowName(
  row: Pick<ConsultantDeadlineRow, 'documentTypeName' | 'title'> &
    Partial<Pick<ConsultantDeadlineRow, 'displayName'>>,
  fallbackLabel: string,
): string {
  const chosenName = row.title?.trim();
  return chosenName || (row.displayName ?? row.documentTypeName ?? fallbackLabel);
}

/** Formats the literal `YYYY-MM-DD` day: building the Date from its parts avoids any UTC shift. */
export function formatIsoDay(isoDay: string, language: string): string {
  const day = parseDateKey(isoDay);
  if (!day) {
    return isoDay;
  }
  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(day);
}

export function getBucketChipColor(bucket: ConsultantDeadlineBucket): BucketChipColor {
  switch (bucket) {
    case 'EXPIRED':
      return 'error';
    case 'EXPIRING':
      return 'warning';
    case 'IN_RENEWAL':
      return 'info';
    case 'OK':
      return 'success';
    case 'NO_DEADLINE':
      return 'default';
  }
}
